import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiServer,
  FiCheckCircle,
  FiAlertCircle,
  FiRefreshCw,
  FiSave,
  FiTrash2,
  FiEye,
  FiEyeOff,
  FiInfo,
  FiLock,
  FiZap,
  FiX,
} from 'react-icons/fi';
import { getBYOOConfig, configureBYOO, testBYOOConnection, deleteBYOOConfig, streamBootstrap, BYOOConfig, BootstrapEvent } from '../../api/byoo';

const BYOOPage: React.FC = () => {
  const { t } = useTranslation('settings');
  const [config, setConfig] = useState<BYOOConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPAT, setShowPAT] = useState(false);

  // Bootstrap SSE state
  const [bootstrapping, setBootstrapping] = useState(false);
  const [bootstrapEvents, setBootstrapEvents] = useState<BootstrapEvent[]>([]);
  const [bootstrapDone, setBootstrapDone] = useState(false);
  const cleanupBootstrap = useRef<(() => void) | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    azure_org_url: '',
    azure_pat: '',
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getBYOOConfig();

      if (result.has_config && result.config) {
        setConfig(result.config);
        setFormData({
          azure_org_url: result.config.azure_org_url,
          azure_pat: '', // Don't prefill PAT for security
        });
      }
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError(t('byoo.errors.businessOnly'));
      } else {
        setError(err.response?.data?.error || t('byoo.errors.loadFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Validation
      if (!formData.azure_org_url || !formData.azure_pat) {
        setError(t('byoo.errors.required'));
        return;
      }

      const result = await configureBYOO({
        azure_org_url: formData.azure_org_url,
        azure_pat: formData.azure_pat,
      });

      setSuccess(result.message);
      setConfig(result.config);

      // Clear PAT from form after successful save
      setFormData(prev => ({ ...prev, azure_pat: '' }));

      // Reload config
      setTimeout(() => loadConfig(), 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || t('byoo.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      setError(null);
      setSuccess(null);

      const result = await testBYOOConnection();

      if (result.success) {
        setSuccess(`✅ ${t('byoo.success.testSuccess', { message: result.message, count: result.validation.projects_count })}`);
      } else {
        setError(result.message);
      }

      // Reload config to update validation status
      setTimeout(() => loadConfig(), 1000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || t('byoo.errors.testFailed'));
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('byoo.deleteConfirm'))) {
      return;
    }

    try {
      setDeleting(true);
      setError(null);
      setSuccess(null);

      const result = await deleteBYOOConfig();
      setSuccess(result.message);
      setConfig(null);
      setFormData({ azure_org_url: '', azure_pat: '' });
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || t('byoo.errors.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  const handleBootstrap = () => {
    setBootstrapping(true);
    setBootstrapDone(false);
    setBootstrapEvents([]);
    const cleanup = streamBootstrap(
      (evt) => {
        setBootstrapEvents(prev => {
          const idx = prev.findIndex(e => e.step === evt.step);
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = evt;
            return next;
          }
          return [...prev, evt];
        });
        if (evt.step === 'done') {
          setBootstrapDone(true);
          setTimeout(() => loadConfig(), 1500);
        }
        if (evt.step === 'error') setBootstrapDone(true);
      },
      () => {
        setBootstrapEvents(prev => [
          ...prev,
          { step: 'error', status: 'error', message: 'Connexion SSE perdue — rechargez la page et réessayez.' }
        ]);
        setBootstrapDone(true);
      }
    );
    cleanupBootstrap.current = cleanup;
  };

  const closeBootstrapModal = () => {
    cleanupBootstrap.current?.();
    setBootstrapping(false);
    setBootstrapEvents([]);
    setBootstrapDone(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600">{t('byoo.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-3 bg-indigo-100 rounded-xl">
            <FiServer className="text-indigo-600" size={28} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('byoo.header.title')}</h1>
            <p className="text-gray-600">{t('byoo.header.subtitle')}</p>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
        <div className="flex items-start gap-3">
          <FiInfo className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-blue-800 w-full">
            <p className="font-semibold mb-2">Prérequis : Personal Access Token Azure DevOps</p>
            <p className="mb-3">
              Votre PAT doit avoir les permissions suivantes sur votre organisation Azure DevOps.
              Dans ADO : <strong>User Settings → Personal Access Tokens → New Token</strong>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                { scope: 'Project and Team',  level: 'Read, Write & Manage', reason: 'Créer le projet terraform-executions' },
                { scope: 'Agent Pools',       level: 'Read & Manage',        reason: 'Créer le pool d\'agents dédié' },
                { scope: 'Code',              level: 'Read & Write',         reason: 'Déployer les pipelines Terraform' },
                { scope: 'Build',             level: 'Read & Execute',       reason: 'Créer et configurer les pipelines CI/CD' },
              ].map(({ scope, level, reason }) => (
                <div key={scope} className="flex items-start gap-2 bg-white rounded-lg border border-blue-200 p-2.5">
                  <FiCheckCircle className="text-blue-500 flex-shrink-0 mt-0.5" size={14} />
                  <div>
                    <span className="font-semibold text-blue-900">{scope}</span>
                    <span className="text-blue-700"> — {level}</span>
                    <p className="text-xs text-blue-600 mt-0.5">{reason}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Status Card */}
      {config && (
        <div className={`mb-6 rounded-xl border p-4 ${
          config.is_validated
            ? 'bg-green-50 border-green-200'
            : 'bg-yellow-50 border-yellow-200'
        }`}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              {config.is_validated ? (
                <FiCheckCircle className="text-green-600" size={24} />
              ) : (
                <FiAlertCircle className="text-yellow-600" size={24} />
              )}
              <div>
                <div className="font-semibold text-gray-900">
                  {config.is_validated ? t('byoo.status.validated') : t('byoo.status.notValidated')}
                </div>
                <div className="text-sm text-gray-600">
                  {t('byoo.status.organization')}: <strong>{config.azure_org_name}</strong>
                </div>
                {config.last_validated_at && (
                  <div className="text-xs text-gray-500 mt-1">
                    {t('byoo.status.lastValidated')}: {new Date(config.last_validated_at).toLocaleString()}
                  </div>
                )}
                {/* Bootstrap / agents status */}
                <div className="mt-2 flex flex-col gap-1.5">
                  {config.bootstrap_status === 'bootstrapped' ? (
                    <>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full w-fit">
                        <FiCheckCircle size={11} /> Pipeline actif
                      </span>
                      {/* Lane agents status */}
                      {!config.lane || config.lane.provision_status == null ? (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-yellow-50 text-yellow-800 text-xs rounded-full border border-yellow-200 w-fit">
                          <FiRefreshCw size={10} className="animate-spin" />
                          Agents en attente d'activation par l'équipe GenIaC
                        </span>
                      ) : config.lane.provision_status === 'provisioning' ? (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full border border-blue-200 w-fit">
                          <FiRefreshCw size={10} className="animate-spin" />
                          Déploiement des agents en cours…
                        </span>
                      ) : config.lane.provision_status === 'ready' && config.lane.is_active ? (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full border border-green-200 w-fit">
                          <FiCheckCircle size={10} />
                          Agents actifs — Infrastructure opérationnelle
                        </span>
                      ) : config.lane.provision_status === 'error' ? (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-red-50 text-red-700 text-xs rounded-full border border-red-200 w-fit">
                          <FiAlertCircle size={10} />
                          Erreur lors du déploiement des agents — contacter le support
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-800 text-xs font-semibold rounded-full w-fit">
                      <FiAlertCircle size={11} /> Pipeline non activé
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handleTest}
                disabled={testing}
                className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-lg transition-colors disabled:opacity-50"
              >
                <FiRefreshCw className={testing ? 'animate-spin' : ''} size={14} />
                {testing ? t('byoo.buttons.testing') : t('byoo.buttons.test')}
              </button>
              {/* Bouton Activer — visible seulement si validé et pas encore bootstrappé */}
              {config.is_validated && config.bootstrap_status !== 'bootstrapped' && (
                <button
                  onClick={handleBootstrap}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
                >
                  <FiZap size={14} />
                  Activer mon pipeline Terraform
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bootstrap SSE Modal */}
      {bootstrapping && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <FiZap className="text-blue-600" size={20} />
                <h2 className="text-lg font-semibold text-gray-900">Activation du pipeline BYOO</h2>
              </div>
              {bootstrapDone && (
                <button onClick={closeBootstrapModal} className="text-gray-400 hover:text-gray-600">
                  <FiX size={20} />
                </button>
              )}
            </div>
            <div className="px-6 py-4 max-h-96 overflow-y-auto space-y-2">
              {bootstrapEvents.length === 0 && (
                <div className="flex items-center gap-3 text-gray-500 text-sm py-4">
                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                  Connexion au serveur...
                </div>
              )}
              {bootstrapEvents.map((evt, i) => (
                <div key={i} className={`flex items-start gap-3 text-sm rounded-lg px-3 py-2 ${
                  evt.status === 'success'  ? 'bg-green-50 text-green-800'  :
                  evt.status === 'warning'  ? 'bg-yellow-50 text-yellow-800' :
                  evt.status === 'error'    ? 'bg-red-50 text-red-800'       :
                  'bg-blue-50 text-blue-800'
                }`}>
                  <span className="flex-shrink-0 mt-0.5">
                    {evt.status === 'running' && <div className="w-3.5 h-3.5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />}
                    {evt.status === 'success' && <FiCheckCircle size={14} />}
                    {evt.status === 'warning' && <FiAlertCircle size={14} />}
                    {evt.status === 'error'   && <FiAlertCircle size={14} />}
                  </span>
                  <span className="leading-tight">{evt.message}</span>
                </div>
              ))}
            </div>
            {bootstrapDone && (
              <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
                {bootstrapEvents[bootstrapEvents.length - 1]?.status === 'success' ? (
                  <p className="text-sm text-green-700 font-medium">
                    Pipeline configuré ! L'équipe GenIaC va activer vos agents dans les prochaines heures. Vous serez notifié dès que votre infrastructure est opérationnelle.
                  </p>
                ) : (
                  <p className="text-sm text-red-700">Vérifiez les erreurs ci-dessus et réessayez.</p>
                )}
                <button
                  onClick={closeBootstrapModal}
                  className="ml-4 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
                >
                  Fermer
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <FiAlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <h3 className="font-semibold text-red-800">{t('byoo.error.title')}</h3>
            <p className="text-red-600 text-sm mt-1">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-500 hover:text-red-700"
          >
            ×
          </button>
        </div>
      )}

      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <FiCheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <h3 className="font-semibold text-green-800">{t('byoo.success.title')}</h3>
            <p className="text-green-600 text-sm mt-1">{success}</p>
          </div>
          <button
            onClick={() => setSuccess(null)}
            className="text-green-500 hover:text-green-700"
          >
            ×
          </button>
        </div>
      )}

      {/* Configuration Form */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">{t('byoo.config.title')}</h2>
          <p className="text-sm text-gray-600 mt-1">
            {config ? t('byoo.config.subtitleEdit') : t('byoo.config.subtitleNew')}
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Azure Org URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('byoo.form.orgUrl')}
            </label>
            <input
              type="text"
              value={formData.azure_org_url}
              onChange={(e) => setFormData({ ...formData, azure_org_url: e.target.value })}
              placeholder={t('byoo.form.orgUrlPlaceholder')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('byoo.form.orgUrlHint')}
            </p>
          </div>

          {/* Personal Access Token */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('byoo.form.pat')}
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2">
                <FiLock className="text-gray-400" />
              </div>
              <input
                type={showPAT ? 'text' : 'password'}
                value={formData.azure_pat}
                onChange={(e) => setFormData({ ...formData, azure_pat: e.target.value })}
                placeholder={config ? t('byoo.form.patPlaceholderKeep') : t('byoo.form.patPlaceholder')}
                className="w-full pl-10 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => setShowPAT(!showPAT)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPAT ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {t('byoo.form.patHint')} <strong>{t('byoo.infoBanner.permissions')}</strong>
            </p>
            {config && (
              <p className="text-xs text-indigo-600 mt-1">
                {t('byoo.form.patCurrent')}: {config.pat_hint}
              </p>
            )}
          </div>

        </div>

        {/* Actions */}
        <div className="p-6 bg-gray-50 border-t border-gray-200 rounded-b-xl flex justify-between">
          {config && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              <FiTrash2 />
              {deleting ? t('byoo.buttons.deleting') : t('byoo.buttons.delete')}
            </button>
          )}

          <div className="ml-auto">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              <FiSave />
              {saving ? t('byoo.buttons.saving') : config ? t('byoo.buttons.update') : t('byoo.buttons.save')}
            </button>
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div className="mt-8 bg-gray-50 rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Comment créer votre Personal Access Token</h3>
        <ol className="space-y-3 text-sm text-gray-700">
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center font-semibold text-xs">1</span>
            <span>Connectez-vous à <a href="https://dev.azure.com" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline font-medium">dev.azure.com</a> avec votre compte administrateur</span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center font-semibold text-xs">2</span>
            <span>Cliquez sur votre avatar en haut à droite → <strong>Personal Access Tokens</strong></span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center font-semibold text-xs">3</span>
            <span>Cliquez sur <strong>+ New Token</strong></span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center font-semibold text-xs">4</span>
            <span>Donnez-lui un nom (ex: <em>GenIaC-BYOO</em>), sélectionnez votre organisation et choisissez une durée d'expiration</span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center font-semibold text-xs">5</span>
            <span>Sous <strong>Scopes</strong>, activez : <em>Project and Team (Read, Write & Manage)</em>, <em>Agent Pools (Read & Manage)</em>, <em>Code (Read & Write)</em>, <em>Build (Read & Execute)</em></span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center font-semibold text-xs">6</span>
            <span>Cliquez sur <strong>Create</strong> et copiez le token — il ne sera plus visible après fermeture</span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center font-semibold text-xs">7</span>
            <span>Collez l'URL de votre organisation et ce token dans le formulaire ci-dessus</span>
          </li>
        </ol>
      </div>
    </div>
  );
};

export default BYOOPage;
